import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useOrders from '../../hooks/useOrders';
import { getSessions } from '../../services/sessions';
import { getCustomers } from '../../services/customers';
import { bulkUpdateOrderStatus } from '../../services/orders';
import { VALID_ORDER_STATUSES, ORDER_STATUS_LABELS } from '../../utils/constants';
import OrderList from '../../components/orders/OrderList';
import OrderLineItemsTable from '../../components/orders/OrderLineItemsTable';
import ProductSummaryTable from '../../components/orders/ProductSummaryTable';
import Alert from '../../components/common/Alert';
import './EmployeeListPage.css';
import './Orders.css';

const STATUS_FILTER_OPTIONS = [
  { value: '',                label: 'All' },
  { value: 'pending',         label: 'Pending' },
  { value: 'confirmed',       label: 'Confirmed' },
  { value: 'in_progress',     label: 'In Progress' },
  { value: 'fulfilled',       label: 'Fulfilled' },
  { value: 'out_for_delivery',label: 'Delivering' },
  { value: 'delivered',       label: 'Delivered' },
  { value: 'cancelled',       label: 'Cancelled' },
];

const TABS = [
  { key: 'orders',     label: 'Orders' },
  { key: 'line-items', label: 'Line Items' },
  { key: 'summary',    label: 'Summary' },
];

const EmployeeOrders = () => {
  const navigate = useNavigate();

  // ── Active tab ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('orders');

  // ── List filters ─────────────────────────────────────────
  const [searchTerm, setSearchTerm]       = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const debounceRef = useRef(null);

  // ── Sessions ─────────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    getSessions({ employee: true })
      .then((data) => setSessions(data))
      .catch(() => setSessions([]));
  }, []);

  // ── Alert ────────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Bulk selection ───────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((ids) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }, []);

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleBulkUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const result = await bulkUpdateOrderStatus([...selectedIds], bulkStatus);
      setAlert({
        type: 'success',
        message: `Updated ${result.updated} order${result.updated !== 1 ? 's' : ''} to "${bulkStatus}".${result.errors.length ? ` ${result.errors.length} failed.` : ''}`,
      });
      setBulkStatus('');
      fetchOrders({ search: searchTerm, status: statusFilter, dateFrom, dateTo, session: sessionFilter });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Bulk update failed.' });
      setBulkStatus('');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── New order company picker ─────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerCompanies, setPickerCompanies] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const pickerDebounceRef = useRef(null);
  const pickerSearchRef = useRef(null);

  // ── Data hook ────────────────────────────────────────────
  const {
    orders,
    loading,
    isRefreshing,
    error,
    fetchOrders: _fetchOrders,
    clearError,
  } = useOrders();

  // Wrap fetchOrders so every filter change automatically clears selection.
  // Any new filter handler added in the future inherits this behaviour for free.
  const fetchOrders = useCallback((params) => {
    setSelectedIds(new Set());
    _fetchOrders(params);
  }, [_fetchOrders]);

  // ── Sync hook errors into alert ───────────────────────────
  useEffect(() => {
    if (error) {
      setAlert({ type: 'error', message: error.message || 'An error occurred.' });
      clearError();
    }
  }, [error, clearError]);

  // ── Cleanup timers on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(pickerDebounceRef.current);
    };
  }, []);

  // ── Debounced search ─────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchOrders({ search: val, status: statusFilter, dateFrom, dateTo, session: sessionFilter });
    }, 300);
  };

  // ── Status filter pills ──────────────────────────────────
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchOrders({ search: searchTerm, status, dateFrom, dateTo, session: sessionFilter });
  };

  // ── Date range inputs ────────────────────────────────────
  const handleDateFrom = (e) => {
    const val = e.target.value;
    setDateFrom(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchOrders({ search: searchTerm, status: statusFilter, dateFrom: val, dateTo, session: sessionFilter });
    }, 300);
  };

  const handleDateTo = (e) => {
    const val = e.target.value;
    setDateTo(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchOrders({ search: searchTerm, status: statusFilter, dateFrom, dateTo: val, session: sessionFilter });
    }, 300);
  };

  const handleClearDates = () => {
    setDateFrom('');
    setDateTo('');
    fetchOrders({ search: searchTerm, status: statusFilter, dateFrom: '', dateTo: '', session: sessionFilter });
  };

  // ── Session filter ────────────────────────────────────────
  const handleSessionFilter = (e) => {
    const val = e.target.value;
    setSessionFilter(val);
    fetchOrders({ search: searchTerm, status: statusFilter, dateFrom, dateTo, session: val });
  };

  const hasDateFilter = dateFrom || dateTo;
  const orderCount    = orders.length;

  // Line-items count: total product rows across all orders
  const lineItemCount = useMemo(
    () => orders.reduce((sum, o) => sum + (o.items?.length || 0), 0),
    [orders]
  );

  // Summary count: distinct product names
  const productCount = useMemo(() => {
    const names = new Set();
    orders.forEach((o) => (o.items || []).forEach((i) => {
      if (i.product_variant?.product_name) names.add(i.product_variant.product_name);
    }));
    return names.size;
  }, [orders]);

  // ── Company picker ───────────────────────────────────────
  const fetchPickerCompanies = useCallback(async (search = '') => {
    setPickerLoading(true);
    try {
      const data = await getCustomers({ search });
      setPickerCompanies(data.results ?? data);
    } catch {
      setPickerCompanies([]);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  const handleOpenPicker = () => {
    setPickerOpen(true);
    setPickerSearch('');
    fetchPickerCompanies('');
  };

  useEffect(() => {
    if (pickerOpen) pickerSearchRef.current?.focus();
  }, [pickerOpen]);

  const handleClosePicker = () => {
    setPickerOpen(false);
    setPickerSearch('');
  };

  const handlePickerSearch = (e) => {
    const val = e.target.value;
    setPickerSearch(val);
    clearTimeout(pickerDebounceRef.current);
    pickerDebounceRef.current = setTimeout(() => fetchPickerCompanies(val), 300);
  };

  const handleSelectCompany = (company) => {
    setPickerOpen(false);
    navigate('/employee/customers/new-order', {
      state: { customerId: company.id, companyName: company.company_name },
    });
  };

  // ── Print QR Sheet ──────────────────────────────────────
  const handlePrintQRSheet = () => {
    const qs = new URLSearchParams();
    if (searchTerm)    qs.set('search', searchTerm);
    if (statusFilter)  qs.set('status', statusFilter);
    if (dateFrom)      qs.set('dateFrom', dateFrom);
    if (dateTo)        qs.set('dateTo', dateTo);
    if (sessionFilter) qs.set('session', sessionFilter);
    const qsStr = qs.toString();
    window.open(`/print-qr-sheet${qsStr ? `?${qsStr}` : ''}`, '_blank');
  };

  const countLabel = activeTab === 'line-items' ? lineItemCount
    : activeTab === 'summary' ? productCount
    : orderCount;
  const countNoun  = activeTab === 'line-items'
    ? (lineItemCount === 1 ? 'line item' : 'line items')
    : activeTab === 'summary'
    ? (productCount  === 1 ? 'product'   : 'products')
    : (orderCount    === 1 ? 'order'     : 'orders');

  return (
    <div className="employee-page orders-page">

      {/* ── Alert ─────────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {/* ── Header ────────────────────────────────────────── */}
      <div className="employee-page__header">
        <p className="employee-page__eyebrow">Employee Portal</p>
        <h1 className="employee-page__title">Orders</h1>
      </div>

      {/* ── Tabs ──────────────────────────────────────────── */}
      <div className="orders-tabs" role="tablist" aria-label="Orders view">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            type="button"
            aria-selected={activeTab === tab.key}
            className={`orders-tab${activeTab === tab.key ? ' orders-tab--active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setSelectedIds(new Set()); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Toolbar: search + date range + count ──────────── */}
      <div className="emp-list__toolbar orders-toolbar">
        <div className="emp-list__toolbar-left">
          <div className="emp-list__search-wrap">
            <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z"
                fill="currentColor"
              />
            </svg>
            <input
              type="text"
              className="emp-list__search"
              placeholder="Search orders or companies…"
              value={searchTerm}
              onChange={handleSearchChange}
              aria-label="Search orders"
            />
          </div>

          {/* ── Date range ── */}
          <div className="orders-date-range">
            <div className="orders-date-field">
              <label className="orders-date-label" htmlFor="orders-date-from">From</label>
              <input
                id="orders-date-from"
                type="date"
                className="orders-date-input"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={handleDateFrom}
                aria-label="Orders placed from date"
              />
            </div>
            <span className="orders-date-sep" aria-hidden="true">–</span>
            <div className="orders-date-field">
              <label className="orders-date-label" htmlFor="orders-date-to">To</label>
              <input
                id="orders-date-to"
                type="date"
                className="orders-date-input"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={handleDateTo}
                aria-label="Orders placed to date"
              />
            </div>
            {hasDateFilter && (
              <button
                type="button"
                className="orders-date-clear"
                onClick={handleClearDates}
                aria-label="Clear date filter"
                title="Clear date filter"
              >
                ×
              </button>
            )}
          </div>

          {/* ── Session filter ── */}
          {sessions.length > 0 && (
            <div className="orders-session-filter">
              <label className="orders-date-label" htmlFor="orders-session-filter">Session</label>
              <select
                id="orders-session-filter"
                className="orders-date-input"
                value={sessionFilter}
                onChange={handleSessionFilter}
                aria-label="Filter by mill session"
              >
                <option value="">All Sessions</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="orders-toolbar__right">
          {countLabel > 0 && (
            <span className="orders-count__label">
              {countLabel} {countNoun}
            </span>
          )}
          {activeTab === 'orders' && orderCount > 0 && (
            <button
              type="button"
              className="orders-qr-btn"
              onClick={handlePrintQRSheet}
            >
              Print QR Sheet
            </button>
          )}
          <button
            type="button"
            className="orders-new-order-btn"
            onClick={handleOpenPicker}
          >
            + New Order
          </button>
        </div>
      </div>

      {/* ── Status filter pills ─────────────────────────────────── */}
      <div className="orders-filters-wrap">
        <div className="emp-list__filters orders-status-filters">
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`emp-list__filter${statusFilter === opt.value ? ' emp-list__filter--active' : ''}`}
              onClick={() => handleStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Summary tab filter context note ───────────────────── */}
      {activeTab === 'summary' && (searchTerm || statusFilter || hasDateFilter) && (() => {
        const parts = [
          searchTerm    && { key: 'search', node: <span>search <strong>"{searchTerm}"</strong></span> },
          statusFilter  && { key: 'status', node: <span>status <strong>{STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label}</strong></span> },
          hasDateFilter && { key: 'date',   node: <span>date range</span> },
        ].filter(Boolean);
        return (
          <p className="orders-summary-filter-note">
            Showing results filtered by{' '}
            {parts.map((part, i) => (
              <span key={part.key}>{part.node}{i < parts.length - 1 ? ', ' : ''}</span>
            ))}
            . Cancelled orders are always excluded.
          </p>
        );
      })()}

      {/* ── Content body ──────────────────────────────────── */}
      <div className="emp-list__body orders-list-body" role="tabpanel">
        {activeTab === 'orders' ? (
          <OrderList
            orders={orders}
            loading={loading}
            error={null}
            isEmployee
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        ) : activeTab === 'line-items' ? (
          <OrderLineItemsTable
            orders={orders}
            loading={loading}
          />
        ) : (
          <ProductSummaryTable
            orders={orders}
            loading={loading}
          />
        )}
        {isRefreshing && <div className="emp-list__refreshing" />}
      </div>

      {/* ── Bulk action bar ─────────────────────────────────── */}
      {selectedIds.size > 0 && createPortal(
        <div className="bulk-action-bar">
          <span className="bulk-action-bar__count">
            {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <select
            className="bulk-action-bar__select"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            aria-label="Select new status"
          >
            <option value="">— Set status —</option>
            {VALID_ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>{ORDER_STATUS_LABELS[s] ?? s}</option>
            ))}
          </select>
          <button
            type="button"
            className="bulk-action-bar__apply"
            onClick={handleBulkUpdate}
            disabled={!bulkStatus || bulkLoading}
          >
            {bulkLoading ? 'Updating…' : `Update ${selectedIds.size} order${selectedIds.size !== 1 ? 's' : ''}`}
          </button>
          <button
            type="button"
            className="bulk-action-bar__cancel"
            onClick={handleClearSelection}
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>,
        document.body
      )}

      {/* ── Company picker modal ────────────────────────────── */}
      {pickerOpen && createPortal(
        <div className="orders-picker-backdrop" onClick={handleClosePicker}>
          <div className="orders-picker" onClick={(e) => e.stopPropagation()}>
            <div className="orders-picker__header">
              <span className="orders-picker__title">Select Customer</span>
              <button
                type="button"
                className="orders-picker__close"
                onClick={handleClosePicker}
                aria-label="Close"
              >×</button>
            </div>
            <div className="orders-picker__search-wrap">
              <input
                ref={pickerSearchRef}
                type="text"
                className="orders-picker__search"
                placeholder="Search companies…"
                value={pickerSearch}
                onChange={handlePickerSearch}
              />
            </div>
            <div className="orders-picker__list">
              {pickerLoading ? (
                <p className="orders-picker__hint">Loading…</p>
              ) : pickerCompanies.length === 0 ? (
                <p className="orders-picker__hint">No companies found.</p>
              ) : (
                pickerCompanies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    className="orders-picker__item"
                    onClick={() => handleSelectCompany(company)}
                  >
                    {company.company_name}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default EmployeeOrders;
